/**
 * Display + validation rules for capacity enforcement and the capacity history (14 Oct 2026).
 * The server is the authority (it refuses a change without a reason, and trunk capacity
 * for anyone but the platform admin); these only keep the portal honest and helpful.
 */
import type {
  ChangeDirection, EnforcementStatus, CapacityHistoryEntry, LimitCheck,
} from 'api/services/capacityLimits.service';

export const MIN_REASON = 5;

/** Shown wherever a trunk figure is changed: nothing reaches the switch yet. */
export const NOT_YET_ENFORCED_COPY = 'Recorded in cpass; not yet enforced on the switch.';

/** Null when the reason is acceptable, else what to tell the user. */
export const reasonError = (reason: string | null | undefined): string | null => {
  const r = (reason || '').trim();
  if (r.length < MIN_REASON) return `A reason is required (at least ${MIN_REASON} characters). It is kept in the capacity history.`;
  if (r.length > 500) return 'The reason must be at most 500 characters.';
  return null;
};

/** Whether an edit moves a capacity figure (so a reason is needed). Unset = unlimited. */
export const capacityChanged = (
  before: { cps_limit?: number | null; max_channels?: number | null } | null | undefined,
  after: { cps_limit?: number | null; max_channels?: number | null },
): boolean => {
  const b = before || {};
  const norm = (v?: number | null) => (v === undefined ? null : v);
  return norm(b.cps_limit) !== norm(after.cps_limit) || norm(b.max_channels) !== norm(after.max_channels);
};

/** Direction of a proposed change, the same rule the server records. Null = no change. */
export const directionOf = (
  before: { cps: number | null; ch: number | null },
  after: { cps: number | null; ch: number | null },
): ChangeDirection | null => {
  const cmp = (o: number | null, n: number | null) => (o === n ? 0 : o === null ? -1 : n === null ? 1 : Math.sign(n - o));
  if (after.cps === null && after.ch === null && (before.cps !== null || before.ch !== null)) return 'REMOVE';
  if (before.cps === null && before.ch === null) return after.cps === null && after.ch === null ? null : 'SET';
  const c = cmp(before.cps, after.cps);
  const h = cmp(before.ch, after.ch);
  const up = c > 0 || h > 0;
  const down = c < 0 || h < 0;
  if (up && !down) return 'INCREASE';
  if (down && !up) return 'DECREASE';
  return up ? 'SET' : null;
};

export const statusLabel: Record<EnforcementStatus, string> = {
  ENFORCED: 'Enforced',
  PARTIAL: 'Partial',
  NOT_ON_SWITCH: 'Not on switch',
  MISMATCH: 'Mismatch',
  NOT_ENFORCEABLE: 'Not enforceable',
  UNVERIFIED: 'Unverified',
};

export const statusTone = (s: EnforcementStatus): string => ({
  ENFORCED: 'bg-green-100 text-green-800',
  PARTIAL: 'bg-amber-100 text-amber-800',
  NOT_ON_SWITCH: 'bg-orange-100 text-orange-800',
  MISMATCH: 'bg-red-100 text-red-800',
  NOT_ENFORCEABLE: 'bg-gray-200 text-gray-700',
  UNVERIFIED: 'bg-slate-200 text-slate-700',
}[s]);

export const directionTone = (d: ChangeDirection): string => ({
  INITIAL: 'bg-slate-100 text-slate-700',
  SET: 'bg-blue-100 text-blue-800',
  INCREASE: 'bg-green-100 text-green-800',
  DECREASE: 'bg-amber-100 text-amber-800',
  REMOVE: 'bg-red-100 text-red-800',
}[d]);

const n = (v: number | null | undefined) => (v === null || v === undefined ? '∞' : String(v));

/** "50 → 40 ch" / "∞ → 2 CPS" — only the axes that moved (both for INITIAL). */
export const describeChange = (e: CapacityHistoryEntry): string => {
  const parts: string[] = [];
  const both = e.direction === 'INITIAL';
  if (both || e.old_max_channels !== e.new_max_channels) parts.push(`${both ? '' : `${n(e.old_max_channels)} → `}${n(e.new_max_channels)} ch`);
  if (both || e.old_cps !== e.new_cps) parts.push(`${both ? '' : `${n(e.old_cps)} → `}${n(e.new_cps)} CPS`);
  return parts.join(' · ') || 'enabled changed';
};

/** Limits sorted so the ones needing attention come first. */
export const byAttention = (rows: LimitCheck[]): LimitCheck[] => {
  const rank: Record<EnforcementStatus, number> = {
    MISMATCH: 0, NOT_ON_SWITCH: 1, UNVERIFIED: 2, PARTIAL: 3, NOT_ENFORCEABLE: 4, ENFORCED: 5,
  };
  return [...rows].sort((a, b) => rank[a.status] - rank[b.status] || a.scope.localeCompare(b.scope)
    || a.scopeValue.localeCompare(b.scopeValue));
};

/** A trunk's display name from the panel's switch list or the providers list. */
export const trunkName = (providerId: string, names: Record<string, string>): string =>
  names[providerId] ? `${names[providerId]} (#${providerId})` : `#${providerId}`;
